import React from "react";
import { Container } from "@material-ui/core";
import PageTitle from "../../../components/pageTitle/PageTitle";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  BarChart3,
  CheckSquare,
  Clock,
  Mail,
  MessageSquare,
  Package,
  PackageX,
  TrendingUp,
  UserCheck,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";

function EmployeeReport(props) {
  const navigate = useNavigate();

  const reportCategories = [
    {
      title: "Time & Attendance",
      reports: [
        {
          title: "Clock-in/out Log",
          description: "Shows when employees started and ended shifts",
          icon: <Clock className="h-6 w-6" />,
          to: "/reports/newreport/checkInCheckOut",
        },
        {
          title: "Shift Variance Report",
          description: "Logged time vs scheduled time",
          icon: <UserCheck className="h-6 w-6" />,
          to: "/reports/newreport/shiftVariance"
        },
        {
          title: "Missed Shifts",
          description: "Logged time vs scheduled time",
          icon: <AlertTriangle className="h-6 w-6" />,
          to: "/reports/newreport/missedShift"
        },
      ],
    },
    {
      title: "Task Completion & Productivity",
      reports: [
        {
          title: "Tasks per Employee",
          description: "Assigned vs completed tasks per worker",
          icon: <CheckSquare className="h-6 w-6" />,
          to: "/reports/newreport/tasksPerEmployee"
        },
      ],
    },
    {
      title: "Issue Handling",
      reports: [
        {
          title: "Issue Resolution Log",
          description: "Issues resolved per team member",
          icon: <AlertCircle className="h-6 w-6" />,
          to: "/reports/newreport/issueResolutionLog"
        },
        {
          title: "Escalation Tracker",
          description: "How often issues were escalated",
          icon: <TrendingUp className="h-6 w-6" />,
            to: "/reports/newreport/escalationTracker"
        },
      ],
    },
    {
      title: "Supply Requests by Employee",
      reports: [
        {
          title: "Employee Supply Requests",
          description: "Supply request volume by worker",
          icon: <Package className="h-6 w-6" />,
          to: "/reports/newreport/empSupplyRequest"
        },
        {
          title: "Urgent Supply Patterns",
          description: "Who requests emergency items most",
          icon: <PackageX className="h-6 w-6" />,
          to: "/reports/newreport/urgentSupplyPatterns"
        },
      ],
    },
    {
      title: "Team Communication Messages",
      reports: [
        {
          title: "Messages Sent per Employee",
          description: "Volume of team messages sent",
          icon: <MessageSquare className="h-6 w-6" />,
          to: "/reports/newreport/messageSendToEmployee"
        },
        {
          title: "Unread Messages",
          description: "Track messages not viewed or acknowledged",
          icon: <Mail className="h-6 w-6" />,
          to: "/reports/newreport/unReadMessage"
        },
      ],
    },
    {
      title: "Employe Summary Reports",
      reports: [
        {
          title: "Overall Performance Report",
          description: "Aggregate view of time, task, and issue handling",
          icon: <BarChart3 className="h-6 w-6" />,
          to: "/reports/newreport/employeeOverAllPerformance"
        },
        {
          title: "Activity Report",
          description:
            "Track user activities including login, check-in, and check-out actions",
          icon: <Activity className="h-6 w-6" />,
          to: "/reports/newreport/activityReports"
        },
      ],
    },
  ];

  const handleNavigate = (to) => {
    navigate(to);
  };

  return (
    <>
      <Container style={{ boxSizing: "content-box", padding: 0 }}>
        <PageTitle
          pageTitle="Teams Reports"
          showBreadcrumb={[{ label: "Home", to: "/" }, { label: "Reports" },  { label: "New Reports" }]}
          BreadCrumbCurrentPageTitle={"Teams Reports"}
          showLastSeen={true}
        ></PageTitle>

        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6">
          <div className="space-y-6">
            <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
              <p className="text-lg text-gray-900 dark:text-white">
                Generate and view reports for team performance and attendance
              </p>
            </div>

            <div className="space-y-8">
              {reportCategories?.map((category, categoryIndex) => (
                <div key={categoryIndex} className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-8">
                    {category?.title}
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {category?.reports?.map((report, reportIndex) => (
                      <Card
                        key={reportIndex}
                        className="dark:shadow-[0_0_12px_rgba(2,8,23,0.8)] hover:shadow-md transition-all duration-200 hover:border-green-500 hover:bg-white dark:bg-[#1A1F2C] dark:hover:bg-gray-800 cursor-pointer group"
                      >
                        <CardHeader className="p-12">
                          <div className="flex items-start space-x-3">
                            <div className="p-8 bg-slate-100 dark:bg-gray-700 rounded-md text-slate-700 dark:text-gray-300 group-hover:bg-slate-100 dark:group-hover:bg-gray-700 group-hover:text-green-500 dark:group-hover:text-green-400 group-hover:ring-1 group-hover:ring-green-500 dark:group-hover:ring-green-400 transition-all">
                              {report?.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <CardTitle className="text-base font-medium text-gray-900 dark:text-white group-hover:text-green-500 dark:group-hover:text-green-400 break-words">
                                {report?.title}
                              </CardTitle>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0 pb-24 ph-24">
                          <CardDescription className="text-sm text-gray-500 dark:text-gray-400 mb-16 leading-relaxed">
                            {report?.description}
                          </CardDescription>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-90 group-hover:border-green-500 group-hover:text-green-500 dark:group-hover:text-green-400 transition-colors"
                            onClick={() => handleNavigate(report?.to)}
                          >
                            Create Report
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </>
  );
}

export default EmployeeReport;